import { useEffect, useState, type RefObject } from 'react';
import { PULL_REST, PULL_THRESHOLD, pullAxis, pullDistance } from '../dnd/pull';
import { haptic } from '../native/platform';
import { manualSync } from '../sync/manual';

export type PullState = 'idle' | 'pulling' | 'refreshing' | 'settling';

const MIN_REFRESH_MS = 700;
const SETTLE_MS = 250;

/**
 * Pull down at the top of a column to sync now. Touch events rather than pointer events:
 * the browser cancels a pointer once it treats the touch as a scroll, but keeps sending
 * touchmove, and a column already at its top has nowhere to scroll anyway.
 */
export function usePullToRefresh(boardRef: RefObject<HTMLElement | null>, indicatorRef: RefObject<HTMLElement | null>, enabled: boolean): PullState {
  const [state, setState] = useState<PullState>('idle');

  useEffect(() => {
    const board = boardRef.current;
    if (!board || !enabled) return;
    let touchId: number | null = null;
    let column: HTMLElement | null = null;
    let x0 = 0;
    let y0 = 0;
    let axis: 'x' | 'y' | null = null;
    let travel = 0;
    let armed = false;
    let busy = false;
    let disposed = false;

    const paint = (px: number) => {
      const el = indicatorRef.current;
      if (!el) return;
      el.style.setProperty('--pull', `${px}px`);
      el.style.setProperty('--pull-p', String(Math.min(1, px / PULL_THRESHOLD)));
      el.toggleAttribute('data-armed', px >= PULL_THRESHOLD);
    };

    const reset = () => {
      touchId = null;
      column = null;
      axis = null;
      travel = 0;
      armed = false;
    };

    const settle = () => {
      paint(0);
      setState('settling');
      setTimeout(() => {
        if (disposed) return;
        busy = false;
        setState('idle');
      }, SETTLE_MS);
    };

    const refresh = async () => {
      busy = true;
      haptic('confirm');
      paint(PULL_REST);
      setState('refreshing');
      const started = Date.now();
      await manualSync();
      const remaining = MIN_REFRESH_MS - (Date.now() - started);
      if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
      if (!disposed) settle();
    };

    const onStart = (e: TouchEvent) => {
      if (busy || touchId !== null) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const target = t.target as Element | null;
      // The grip starts a reorder drag; everything else on a column may start a pull.
      if (target?.closest('[data-grip]')) return;
      const col = target?.closest<HTMLElement>('[data-col]');
      if (!col || col.scrollTop > 0) return;
      touchId = t.identifier;
      column = col;
      x0 = t.clientX;
      y0 = t.clientY;
    };

    const onMove = (e: TouchEvent) => {
      if (touchId === null) return;
      const t = Array.from(e.changedTouches).find((c) => c.identifier === touchId);
      if (!t) return;
      const dx = t.clientX - x0;
      const dy = t.clientY - y0;
      if (axis === null) {
        axis = pullAxis(dx, dy);
        if (axis === 'x' || (axis === 'y' && dy < 0)) {
          reset();
          return;
        }
        if (axis === null) return;
        setState('pulling');
      }
      if (column && column.scrollTop > 0) {
        // The browser took the touch as a scroll after all.
        paint(0);
        reset();
        setState('idle');
        return;
      }
      travel = pullDistance(dy);
      const nowArmed = travel >= PULL_THRESHOLD;
      if (nowArmed !== armed) {
        armed = nowArmed;
        haptic('tick');
      }
      paint(travel);
    };

    const onEnd = (e: TouchEvent) => {
      if (touchId === null) return;
      if (!Array.from(e.changedTouches).some((c) => c.identifier === touchId)) return;
      const fire = e.type === 'touchend' && axis === 'y' && travel >= PULL_THRESHOLD;
      const wasPulling = axis === 'y';
      reset();
      if (fire) void refresh();
      else if (wasPulling) {
        busy = true;
        settle();
      }
    };

    board.addEventListener('touchstart', onStart, { passive: true });
    board.addEventListener('touchmove', onMove, { passive: true });
    board.addEventListener('touchend', onEnd, { passive: true });
    board.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      disposed = true;
      board.removeEventListener('touchstart', onStart);
      board.removeEventListener('touchmove', onMove);
      board.removeEventListener('touchend', onEnd);
      board.removeEventListener('touchcancel', onEnd);
    };
  }, [boardRef, indicatorRef, enabled]);

  return state;
}
